import React from 'react';
import { useConversation } from '../../../contexts/ConversationContext';
import { usePersonaToolProgress, type PhaseEvent } from '../../../hooks/usePersonaToolProgress';
import { EMPTY_PHASE_EVENTS } from '../../../contexts/ChatDagRuntimeContext';

// Lifts usePersonaToolProgress so there's ONE WS subscription per chat
// session. Pipe gates the hook via state.status === 'loading' (idle reads
// shouldn't hold a subscription) and writes outputs back to the orchestrator
// state so both the DAG runtime context and the conversation surface render
// from a single source.
export const LiveProgressPipe: React.FC<{
  sessionId: string;
  onUpdate: (
    loadingStatus: string,
    phaseEvents: PhaseEvent[],
    wsLastEventMs: number | null,
    inFlightTurnId: string | null,
    errorTurnId: string | null,
  ) => void;
}> = ({ sessionId, onUpdate }) => {
  const { state } = useConversation();
  const enabled = state.status === 'loading';
  const { loadingStatus, phaseEvents } = usePersonaToolProgress({
    sessionId,
    enabled,
  });
  // wsLastEventMs: bump when phaseEvents array grows. Used by the DAG
  // WS-health watchdog to detect a silent socket.
  const lastCountRef = React.useRef(0);
  const lastEventMsRef = React.useRef<number | null>(null);
  // Find the most recent user message without a following assistant
  // message (the "open" turn). Used for both inFlightTurnId (loading)
  // and errorTurnId (error).
  const openTurnUserId = React.useMemo(() => {
    const msgs = state.messages;
    for (let i = msgs.length - 1; i >= 0; i--) {
      if (msgs[i].role === 'user') {
        const hasFollowingAsst = msgs
          .slice(i + 1)
          .some((m) => m.role === 'assistant');
        return hasFollowingAsst ? null : msgs[i].id;
      }
    }
    return null;
  }, [state.messages]);
  const inFlightTurnId = enabled ? openTurnUserId : null;
  const errorTurnId = state.status === 'error' ? openTurnUserId : null;
  React.useEffect(() => {
    if (!enabled) {
      lastCountRef.current = 0;
      lastEventMsRef.current = null;
      onUpdate('', EMPTY_PHASE_EVENTS, null, null, errorTurnId);
      return;
    }
    if (phaseEvents.length !== lastCountRef.current) {
      lastCountRef.current = phaseEvents.length;
      lastEventMsRef.current = Date.now();
    }
    onUpdate(
      loadingStatus,
      phaseEvents,
      lastEventMsRef.current,
      inFlightTurnId,
      errorTurnId,
    );
  }, [enabled, loadingStatus, phaseEvents, onUpdate, inFlightTurnId, errorTurnId]);
  return null;
};
